import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight, Menu, X } from "lucide-react";

const links = [
    { label: "Home", href: "#home", id: "home" },
    { label: "My Work", href: "#work", id: "work" },
    { label: "About Me", href: "#about", id: "about" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState("home");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 40);
            const y = window.scrollY + 240;
            let current = "home";
            for (const l of links) {
                const el = document.getElementById(l.id);
                if (el && el.offsetTop <= y) current = l.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        const scroll = () => {
            if (window.__lenis) window.__lenis.scrollTo(href, { offset: -90 });
            else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
        };
        if (location.pathname !== "/" && !document.querySelector(href)) {
            navigate("/");
            setTimeout(scroll, 450);
        } else {
            scroll();
        }
    };

    return (
        <motion.header
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-8"
            data-testid="navbar"
        >
            <nav
                className={`mx-auto flex max-w-6xl items-center justify-between rounded-full px-5 py-2.5 transition-all duration-500 ${
                    scrolled ? "glass-nav" : "border border-transparent"
                }`}
            >
                <a
                    href="#home"
                    onClick={(e) => go(e, "#home")}
                    className="flex items-center gap-2.5"
                    data-testid="nav-logo"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-viol text-sm font-extrabold text-white shadow-[0_4px_14px_rgba(112,71,245,0.4)]">
                        SJ
                    </span>
                    <span className="hidden text-sm font-bold tracking-tight text-ink sm:block">
                        Sushma Jaykar
                    </span>
                </a>

                <div className="hidden items-center gap-1 lg:flex">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={(e) => go(e, l.href)}
                            className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                active === l.id ? "text-ink" : "text-mist hover:text-ink"
                            }`}
                            data-testid={`nav-link-${l.id}`}
                        >
                            {l.label}
                            {active === l.id && (
                                <motion.span
                                    layoutId="nav-active-dot"
                                    className="absolute -bottom-0.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-sun"
                                    transition={{ type: "spring", stiffness: 350, damping: 28 }}
                                />
                            )}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-2.5">
                    <a
                        href="/resume/Sushma_Jaykar_CV_2026.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group hidden items-center gap-1.5 rounded-full border-2 border-viol/50 px-4 py-2 text-sm font-bold text-viol transition-all duration-300 hover:border-viol hover:bg-viol hover:text-white lg:flex"
                        data-testid="nav-link-resume"
                    >
                        Resume
                        <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
                    </a>
                    <a
                        href="#contact"
                        onClick={(e) => go(e, "#contact")}
                        className="btn-primary-grad group hidden items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:-translate-y-0.5 sm:flex"
                        data-testid="nav-connect-button"
                    >
                        Let's Connect
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <button
                        onClick={() => setOpen(!open)}
                        className="flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-lav-100 lg:hidden"
                        data-testid="nav-mobile-menu-button"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -12, scale: 0.98 }}
                        transition={{ duration: 0.25 }}
                        className="glass-nav mx-auto mt-2 max-w-6xl rounded-3xl p-4 lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={(e) => go(e, l.href)}
                                className={`flex items-center justify-between rounded-2xl px-4 py-3 text-base font-semibold transition-colors hover:bg-lav-100 ${
                                    active === l.id ? "text-viol" : "text-ink"
                                }`}
                                data-testid={`nav-mobile-link-${l.id}`}
                            >
                                {l.label}
                                {active === l.id && <span className="h-2 w-2 rounded-full bg-sun" />}
                            </a>
                        ))}
                        <a
                            href="/resume/Sushma_Jaykar_CV_2026.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex items-center justify-center gap-2 rounded-2xl border-2 border-viol/50 px-4 py-3 text-base font-bold text-viol"
                            data-testid="nav-mobile-link-resume"
                        >
                            Resume <ArrowDown className="h-4 w-4" />
                        </a>
                        <a
                            href="#contact"
                            onClick={(e) => go(e, "#contact")}
                            className="btn-primary-grad mt-2 flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-base font-bold text-white"
                            data-testid="nav-mobile-connect-button"
                        >
                            Let's Connect <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
